import React, { useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { DiffChangeLocation } from '../services/guidelineSideBySideDiff';

export interface OverviewMarker {
  changeKey: string;
  changeIndex: number | undefined;
  hasRemove: boolean;
  hasAdd: boolean;
  topPct: number;
  heightPct: number;
}

export function ChangeNavigator({
  locations,
  activeIndex,
  onSelect,
}: {
  locations: DiffChangeLocation[];
  activeIndex: number;
  onSelect: (index: number) => void;
}) {
  if (locations.length === 0) return null;

  const active = locations[activeIndex] ?? locations[0];
  const kindLabel =
    active.kind === 'remove' ? '削除' : active.kind === 'add' ? '追加' : '変更';

  return (
    <div className="glsbc-change-nav" role="navigation" aria-label="変更箇所へ移動">
      <span className="glsbc-change-nav-count">
        変更箇所 {activeIndex + 1} / {locations.length}
      </span>
      <span className={`glsbc-change-nav-kind glsbc-change-nav-kind-${active.kind}`}>
        {kindLabel}
      </span>
      <span className="glsbc-change-nav-summary" title={active.summary}>
        {active.summary}
      </span>
      <div className="glsbc-change-nav-actions">
        <button
          type="button"
          className="glsbc-change-nav-btn"
          disabled={activeIndex <= 0}
          onClick={() => onSelect(activeIndex - 1)}
          aria-label="前の変更箇所へ"
        >
          <ChevronUp size={14} />
          前へ
        </button>
        <button
          type="button"
          className="glsbc-change-nav-btn"
          disabled={activeIndex >= locations.length - 1}
          onClick={() => onSelect(activeIndex + 1)}
          aria-label="次の変更箇所へ"
        >
          <ChevronDown size={14} />
          次へ
        </button>
      </div>
    </div>
  );
}

export function DiffOverviewRuler({
  scrollRef,
  markers,
  viewportTopPct,
  viewportHeightPct,
  onSelectChange,
  onScrollSync,
  onScrollToRatio,
}: {
  scrollRef: React.RefObject<HTMLElement | null>;
  markers: OverviewMarker[];
  viewportTopPct: number;
  viewportHeightPct: number;
  onSelectChange: (changeIndex: number) => void;
  onScrollSync: () => void;
  /** When set, scroll both panes (e.g. synced CodeMirror views). */
  onScrollToRatio?: (ratio: number) => void;
}) {
  const jumpToClientY = useCallback(
    (clientY: number, rulerEl: HTMLElement) => {
      const rect = rulerEl.getBoundingClientRect();
      if (rect.height <= 0) return;
      const ratio = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));

      if (onScrollToRatio) {
        onScrollToRatio(ratio);
        onScrollSync();
        return;
      }

      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
      scrollEl.scrollTop = ratio * maxScroll;
      onScrollSync();
    },
    [scrollRef, onScrollSync, onScrollToRatio]
  );

  const onRulerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.glsbc-overview-marker')) return;
    const ruler = e.currentTarget;
    jumpToClientY(e.clientY, ruler);

    const onMove = (ev: PointerEvent) => jumpToClientY(ev.clientY, ruler);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  };

  return (
    <div
      className="glsbc-overview-ruler"
      aria-label="スクロール位置と変更箇所"
      onPointerDown={onRulerPointerDown}
    >
      <div
        className="glsbc-overview-viewport"
        style={{
          top: `${viewportTopPct}%`,
          height: `${viewportHeightPct}%`,
        }}
      />
      {markers.map((marker) => (
        <button
          key={marker.changeKey}
          type="button"
          className="glsbc-overview-marker"
          style={{
            top: `${marker.topPct}%`,
            height: `${marker.heightPct}%`,
          }}
          title="変更箇所へ移動"
          onClick={(e) => {
            e.stopPropagation();
            if (marker.changeIndex !== undefined) onSelectChange(marker.changeIndex);
          }}
        >
          {marker.hasRemove && (
            <span
              className={`glsbc-overview-seg glsbc-overview-seg-remove ${
                marker.hasAdd ? 'glsbc-overview-seg-half' : 'glsbc-overview-seg-full'
              }`}
            />
          )}
          {marker.hasAdd && (
            <span
              className={`glsbc-overview-seg glsbc-overview-seg-add ${
                marker.hasRemove ? 'glsbc-overview-seg-half' : 'glsbc-overview-seg-full'
              }`}
            />
          )}
        </button>
      ))}
    </div>
  );
}
